'use client';

import { usePathname } from 'next/navigation';
import { useState, useEffect } from 'react';

/**
 * Parse YYYY-MM-DD date string to Date object
 * @param {string} dateStr - Date string in YYYY-MM-DD format (e.g., "2026-01-30")
 * @returns {Date|null} Parsed date or null if invalid
 */
function parseDate(dateStr) {
  if (!dateStr) return null;
  try {
    const parts = dateStr.split('-');
    if (parts.length !== 3) return null;
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
    const day = parseInt(parts[2], 10);
    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
    const date = new Date(year, month, day);
    // Validate the date is valid
    if (date.getDate() !== day || date.getMonth() !== month || date.getFullYear() !== year) {
      return null;
    }
    return date;
  } catch {
    return null;
  }
}

/**
 * Check if alert should be visible based on expiration date
 * @param {string|null} alertMessage - The alert message text
 * @param {string|null} alertExpires - Expiration date in YYYY-MM-DD format (e.g., "2026-01-30")
 * @returns {boolean} True if alert should be visible
 */
function isAlertVisible(alertMessage, alertExpires) {
  // Must have alert message
  if (!alertMessage || !alertMessage.trim()) {
    return false;
  }

  // If no expiration date, don't show (safety measure)
  if (!alertExpires) {
    return false;
  }
  const expiryDate = parseDate(alertExpires);
  if (!expiryDate) {
    return false;
  }
  const now = new Date();
  // Set time to start of day for comparison (no timezone handling per requirements)
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const expiry = new Date(expiryDate.getFullYear(), expiryDate.getMonth(), expiryDate.getDate());

  // Visible if current date is before expiration date
  return today < expiry;
}
export default function AlertBanner() {
  const pathname = usePathname();
  const [alertData, setAlertData] = useState({
    alertMessage: null,
    alertExpires: null
  });
  useEffect(() => {
    // Check if we're on a heart detail page
    const heartPageMatch = pathname?.match(/^\/hearts\/([^/]+)$/);
    if (!heartPageMatch) {
      return;
    }
    const slug = heartPageMatch[1];

    // Fetch heart data to get alert information
    const fetchHeartData = async () => {
      try {
        const response = await fetch(`/api/heart/${encodeURIComponent(slug)}`, {
          cache: 'no-store'
        });
        if (!response.ok) {
          setAlertData({
            alertMessage: null,
            alertExpires: null
          });
          return;
        }
        const result = await response.json();
        if (result.success && result.data) {
          setAlertData({
            alertMessage: result.data.alertMessage || null,
            alertExpires: result.data.alertExpires || null
          });
        } else {
          setAlertData({
            alertMessage: null,
            alertExpires: null
          });
        }
      } catch (error) {
        console.error('Error fetching heart alert data:', error);
        setAlertData({
          alertMessage: null,
          alertExpires: null
        });
      }
    };
    fetchHeartData();
  }, [pathname]);
  if (!isAlertVisible(alertData.alertMessage, alertData.alertExpires)) {
    return null;
  }
  return <div className="w-full bg-[#DF014A] text-white">
      <div className="container-wrap py-3">
        <div role="status" aria-live="polite" className="flex items-center gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#C40041] flex items-center justify-center">
            <svg className="h-5 w-5" viewBox="0 0 17 15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M0.776648 14.1429C0.632601 14.1429 0.501648 14.1068 0.383791 14.0348C0.265934 13.9628 0.174267 13.8679 0.108791 13.75C0.043315 13.6321 0.00730311 13.5045 0.000755495 13.367C-0.00579212 13.2295 0.0302198 13.0952 0.108791 12.9643L7.37665 0.392857C7.45522 0.261905 7.55671 0.16369 7.68111 0.0982143C7.80552 0.0327381 7.9332 0 8.06415 0C8.1951 0 8.32278 0.0327381 8.44718 0.0982143C8.57159 0.16369 8.67308 0.261905 8.75165 0.392857L16.0195 12.9643C16.0981 13.0952 16.1341 13.2295 16.1275 13.367C16.121 13.5045 16.085 13.6321 16.0195 13.75C15.954 13.8679 15.8624 13.9628 15.7445 14.0348C15.6266 14.1068 15.4957 14.1429 15.3516 14.1429H0.776648ZM8.06415 11.7857C8.28677 11.7857 8.47337 11.7104 8.62397 11.5598C8.77456 11.4092 8.84986 11.2226 8.84986 11C8.84986 10.7774 8.77456 10.5908 8.62397 10.4402C8.47337 10.2896 8.28677 10.2143 8.06415 10.2143C7.84153 10.2143 7.65492 10.2896 7.50433 10.4402C7.35373 10.5908 7.27843 10.7774 7.27843 11C7.27843 11.2226 7.35373 11.4092 7.50433 11.5598C7.65492 11.7104 7.84153 11.7857 8.06415 11.7857ZM8.06415 9.42857C8.28677 9.42857 8.47337 9.35327 8.62397 9.20268C8.77456 9.05208 8.84986 8.86548 8.84986 8.64286V6.28571C8.84986 6.0631 8.77456 5.87649 8.62397 5.72589C8.47337 5.5753 8.28677 5.5 8.06415 5.5C7.84153 5.5 7.65492 5.5753 7.50433 5.72589C7.35373 5.87649 7.27843 6.0631 7.27843 6.28571V8.64286C7.27843 8.86548 7.35373 9.05208 7.50433 9.20268C7.65492 9.35327 7.84153 9.42857 8.06415 9.42857Z" fill="white" />
            </svg>
          </div>
          <div className="flex-1 min-w-0 font-outfit text-sm md:text-base font-semibold leading-relaxed whitespace-normal break-words">
            {alertData?.alertMessage}
          </div>
        </div>
      </div>
    </div>;
}